import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface BillingSnapshot {
  id: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  active_member_count: number;
  new_prospect_count: number;
  member_unit_price: number;
  prospect_unit_price: number;
  member_total: number;
  prospect_total: number;
  grand_total: number;
  minimum_fee: number;
  final_total: number;
  status: "draft" | "sent" | "paid" | "overdue" | "voided" | "refunded";
  payment_provider: string | null;
  payment_provider_id: string | null;
  payment_url: string | null;
  due_date: string | null;
  generated_at: string;
  sent_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  refunded_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface UseBillingSnapshotsOptions {
  tenantId?: string;
  status?: BillingSnapshot["status"][];
  periodStartFrom?: string;
  periodStartTo?: string;
  limit?: number;
}

export function useBillingSnapshots(opts: UseBillingSnapshotsOptions = {}) {
  const { tenantId, status, periodStartFrom, periodStartTo, limit } = opts;
  const [snapshots, setSnapshots] = useState<BillingSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  const refetch = useCallback(() => setRefetchToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      let q = supabase.from("billing_snapshots")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit ?? 100);
      if (tenantId) q = q.eq("tenant_id", tenantId);
      if (status && status.length > 0) q = q.in("status", status);
      if (periodStartFrom) q = q.gte("period_start", periodStartFrom);
      if (periodStartTo) q = q.lte("period_start", periodStartTo);

      const { data, error: e } = await q;
      if (cancelled) return;
      if (e) setError(e.message);
      else setSnapshots((data ?? []) as BillingSnapshot[]);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [tenantId, status, periodStartFrom, periodStartTo, limit, refetchToken]);

  return { snapshots, loading, error, refetch };
}
